import { Stack } from '@mui/material'
import ConfirmationDialog from 'components/core/confirmation/dialog/index'
import { TextField } from 'components/core/textfield'
import React, { ReactNode, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

export type DeleteConfirmationDialogProps = {
    open: boolean
    itemName: string
    details?: ReactNode
    onConfirm: () => void
    onCancel: () => void
    action?: string
    title?: string
    isActionLoading?: boolean
}

export const DeleteConfirmationDialog = ({
    open,
    itemName,
    details,
    onConfirm,
    onCancel,
    action,
    title,
    isActionLoading,
}: DeleteConfirmationDialogProps): React.ReactElement => {
    const intl = useIntl()

    if (!action) {
        action = intl.formatMessage({ id: 'delete_confirmation_dialog_action_string', defaultMessage: 'Delete' })
    }

    if (!title) {
        title = `${action} ${itemName}`
    }

    return (
        <ConfirmationDialog
            open={open}
            actionLabel={action}
            title={title}
            details={details}
            onConfirm={onConfirm}
            onCancel={onCancel}
            color="error"
            variant="contained"
            isActionLoading={isActionLoading}
        />
    )
}

const deleteString = 'DELETE'

export const DeleteConfirmationDialogWithTextInput = ({
    open,
    itemName,
    details,
    onConfirm,
    onCancel,
    action,
    title,
    isActionLoading,
}: DeleteConfirmationDialogProps): React.ReactElement => {
    const intl = useIntl()

    const [inputValid, setInputValid] = useState(true)
    const [helperText, setHelperText] = useState<string>('')
    const itemNameInput = useRef<HTMLInputElement>(null)

    if (!action) {
        action = intl.formatMessage({ id: 'delete_confirmation_dialog_action_string', defaultMessage: 'Delete' })
    }

    if (!title) {
        title = intl.formatMessage(
            {
                id: 'delete_confirmation_dialog_title',
                defaultMessage: '{action} {item}',
            },
            {
                item: itemName,
                action,
            },
        )
    }

    const validateInput = (): boolean => {
        const inputText = itemNameInput.current?.value.trim()
        const isValid = inputText === 'DELETE'
        setInputValid(isValid)
        if (isValid) {
            setHelperText('')
            return true
        }
        setHelperText(
            intl.formatMessage({
                id: 'delete_confirmation_dialog_please_type_delete',
                defaultMessage: 'Please type {delete} to confirm',
            }, { delete: deleteString }),
        )
        return false
    }

    const onDeleteConfirm = (): void => {
        if (!validateInput()) {
            return
        }

        onConfirm()
    }

    const verifyTextField = (
        <TextField
            label={intl.formatMessage({
                id: 'delete_confirmation_dialog_type_delete',
                defaultMessage: 'Type {delete} to confirm',
            }, { delete: deleteString })}
            onBlur={validateInput}
            error={!inputValid}
            helperText={helperText}
            inputRef={itemNameInput}
            onReturn={onDeleteConfirm}
            id="delete-confirmation-dialog--verify-textfield"
        />
    )

    return (
        <ConfirmationDialog
            open={open}
            actionLabel={action}
            title={title}
            details={
                <Stack direction="column" spacing={2}>
                    {details}
                    {verifyTextField}
                </Stack>
            }
            onConfirm={onDeleteConfirm}
            onCancel={onCancel}
            isActionLoading={isActionLoading}
        />
    )
}
